import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import OAuthButtons from '../../../components/ui/OAuthButtons';

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, authError, clearError } = useAuth();
  
  // Get the intended destination from location state, default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);



  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (authError) {
      clearError();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    clearError();

    try {
      const result = await signIn(formData.email, formData.password);

      if (result?.success) {
        navigate(from, { replace: true });
      } else {
        // Error is already set in AuthContext
        console.log('Login failed:', result?.error);
      }
    } catch (error) {
      console.log('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };



  const handleOAuthSuccess = () => {
    // OAuth sign in successful - navigation handled by AuthContext
    navigate(from, { replace: true });
  };

  const handleOAuthError = (error) => {
    // OAuth error handling - error already set in AuthContext
    console.error('OAuth error:', error);
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* OAuth Buttons */}
      <div className="mb-6">
        <OAuthButtons
          mode="signin"
          disabled={isLoading}
          onSuccess={handleOAuthSuccess}
          onError={handleOAuthError}
        />
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Error Message */}
        {authError && (
          <div className="p-4 bg-error-50 border border-error-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <Icon name="AlertCircle" size={16} className="text-error mt-0.5 flex-shrink-0" />
              <div className="text-sm text-error whitespace-pre-line">
                {authError}
              </div>
            </div>
          </div>
        )}



        {/* Email Field */}
        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-text-primary">
            Email Address
          </label>
          <div className="relative">
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email address"
              className={`w-full pl-10 ${errors.email ? 'border-error focus:ring-error' : ''}`}
              disabled={isLoading}
              required
            />
            <Icon 
              name="Mail" 
              size={18} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" 
            />
          </div>
          {errors.email && (
            <p className="text-sm text-error flex items-center space-x-1">
              <Icon name="AlertCircle" size={14} />
              <span>{errors.email}</span>
            </p>
          )}
        </div>

        {/* Password Field */}
        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-medium text-text-primary">
            Password
          </label>
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              className={`w-full pl-10 pr-10 ${errors.password ? 'border-error focus:ring-error' : ''}`}
              disabled={isLoading}
              required
            />
            <Icon 
              name="Lock" 
              size={18} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" 
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-primary transition-micro"
              disabled={isLoading}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-error flex items-center space-x-1">
              <Icon name="AlertCircle" size={14} />
              <span>{errors.password}</span>
            </p>
          )}
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="flex items-center justify-between">
          <label className="flex items-center space-x-2 cursor-pointer">
            <Input
              type="checkbox"
              className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              disabled={isLoading}
            />
            <span className="text-sm text-text-secondary">Remember me</span>
          </label>
          <button
            type="button"
            className="text-sm text-primary hover:text-primary-600 transition-micro"
            disabled={isLoading}
          >
            Forgot Password?
          </button>
        </div>

        {/* Sign In Button */}
        <Button
          type="submit"
          variant="primary"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
          className="h-12"
        >
          {isLoading ? 'Signing In...' : 'Sign In'}
        </Button>

        {/* Sign Up Link */}
        <div className="text-center text-sm text-text-secondary">
          Don't have an account?{' '}
          <button
            type="button"
            onClick={() => navigate('/signup')}
            className="text-primary hover:text-primary-600 font-medium transition-colors"
            disabled={isLoading}
          >
            Sign up here
          </button>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;